import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DEFAULT_SECONDARY_NAVBAR } from '../common/UnifiedHeader';
import { DEFAULT_FAQ_SECTION } from './FaqPage';
import { AVAILABLE_FONTS, getFontFamilyCss } from '../../utils/fontHelper';

/**
 * 1. PANEL ADMINISTRADOR DE LA BARRA SECUNDARIA / MEGA-MENÚ COMERCIAL
 */
export const SecondaryNavBarAdminPanel = ({
  config,
  setConfig,
  rubros = [],
  selectedCompanyId
}) => {
  const secNav = {
    ...DEFAULT_SECONDARY_NAVBAR,
    ...(config.secondaryNavBar || {}),
    items: Array.isArray(config.secondaryNavBar?.items)
      ? config.secondaryNavBar.items
      : DEFAULT_SECONDARY_NAVBAR.items
  };

  const [expandedItemIdx, setExpandedItemIdx] = useState(0);
  const [expandedRubroCustomCode, setExpandedRubroCustomCode] = useState(null);
  const [articleSearchQuery, setArticleSearchQuery] = useState('');
  const [articleSearchResults, setArticleSearchResults] = useState([]);
  const [searchingArticles, setSearchingArticles] = useState(false);
  const [familiasList, setFamiliasList] = useState([]);

  // Cargar familias (mfam000) de la empresa activa para vincularlas a cada rubro
  useEffect(() => {
    let active = true;
    const fetchFamilias = async () => {
      try {
        const res = await axios.get('/api/familias/', {
          params: selectedCompanyId ? { company: selectedCompanyId } : {},
          headers: selectedCompanyId ? { 'X-Company-ID': String(selectedCompanyId) } : {}
        });
        if (!active) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setFamiliasList(data);
      } catch {
        if (active) setFamiliasList([]);
      }
    };
    fetchFamilias();
    return () => {
      active = false;
    };
  }, [selectedCompanyId]);

  // Rubros enriquecidos con sus familias de la BD
  const rubrosWithFamilias = useMemo(() => {
    const famsByRubro = {};
    (familiasList || []).forEach(f => {
      const cr = String(f.c_rubro || '').trim();
      const cf = String(f.c_famil || '').trim();
      const df = String(f.d_famil || '').trim();
      if (!cr || !cf || !df) return;
      if (!famsByRubro[cr]) famsByRubro[cr] = [];
      if (!famsByRubro[cr].some(x => String(x.c_famil).trim() === cf)) {
        famsByRubro[cr].push({ c_famil: cf, d_famil: df, c_rubro: cr });
      }
    });

    const activeRubros = (rubros || []).filter(r => {
      if (!r) return false;
      if (r.c_situa === 9 || Number(r.c_situa) === 9) return false;
      if (r.q_articulos !== undefined && Number(r.q_articulos) <= 0) return false;
      const name = String(r.d_rubro || '').trim().toLowerCase();
      if (name === 'productos viejos') return false;
      return true;
    });

    return activeRubros.map(r => {
      const cr = String(r.c_rubro || '').trim();
      const existing = Array.isArray(r.familias) ? r.familias : [];
      const extra = famsByRubro[cr] || [];
      const map = new Map();
      [...existing, ...extra].forEach(fm => {
        const cf = String(fm?.c_famil || '').trim();
        const df = String(fm?.d_famil || '').trim();
        if (cf && df && !map.has(cf)) {
          map.set(cf, { c_famil: cf, d_famil: df, c_rubro: cr });
        }
      });
      return {
        ...r,
        c_rubro_clean: cr,
        familias: Array.from(map.values()).sort((a, b) =>
          (a.d_famil || '').localeCompare(b.d_famil || '', 'es-AR')
        )
      };
    });
  }, [rubros, familiasList]);

  const updateSecNav = (patch) => {
    setConfig(prev => ({
      ...prev,
      secondaryNavBar: {
        ...DEFAULT_SECONDARY_NAVBAR,
        ...(prev.secondaryNavBar || {}),
        ...patch
      }
    }));
  };

  const updateItem = (idx, patch) => {
    const nextItems = [...(secNav.items || [])];
    nextItems[idx] = { ...nextItems[idx], ...patch };
    updateSecNav({ items: nextItems });
  };

  const handleAddItem = (type = 'direct') => {
    const newItem =
      type === 'megamenu'
        ? {
            id: `sec-mega-${Date.now()}`,
            label: 'Cajas de Cartón',
            enabled: true,
            type: 'megamenu',
            highlight: false,
            badgeText: '',
            badgeColor: '#ef4444',
            fontFamily: 'inherit',
            lineHeight: '1.45',
            itemSpacing: '8px',
            leftLinks: [],
            columns: [
              {
                id: `col-${Date.now()}-1`,
                title: 'Cartón Corrugado',
                enabled: true,
                targetType: 'rubro',
                targetValue: '',
                showMoreLink: true,
                moreLinkText: 'Ver más',
                subItems: [
                  {
                    id: `sub-${Date.now()}-1`,
                    label: 'Corrugado Simple',
                    targetType: 'search',
                    targetValue: 'Corrugado Simple',
                    enabled: true
                  }
                ]
              }
            ]
          }
        : type === 'rubros_dropdown'
        ? {
            id: `sec-rub-${Date.now()}`,
            label: 'Rubros Destacados',
            enabled: true,
            type: 'rubros_dropdown',
            rubrosMode: 'all',
            selectedRubros: [],
            showDbFamilias: true,
            excludedFamiliasByRubro: {},
            customSubItemsByRubro: {},
            columnsCount: 3,
            showViewAllLink: true,
            viewAllText: 'Ver más',
            highlight: false,
            badgeText: '',
            fontFamily: 'inherit',
            lineHeight: '1.45',
            itemSpacing: '8px'
          }
        : {
            id: `sec-dir-${Date.now()}`,
            label: 'Nuevo Acceso Directo',
            enabled: true,
            type: 'direct',
            targetType: 'search',
            targetValue: '',
            selectedArticles: [],
            highlight: false,
            badgeText: '',
            badgeColor: '#ef4444'
          };

    const nextItems = [...(secNav.items || []), newItem];
    updateSecNav({ items: nextItems });
    setExpandedItemIdx(nextItems.length - 1);
  };

  const handleRemoveItem = (idx) => {
    const nextItems = (secNav.items || []).filter((_, i) => i !== idx);
    updateSecNav({ items: nextItems });
    if (expandedItemIdx === idx) setExpandedItemIdx(null);
  };

  const handleMoveItem = (idx, dir) => {
    const target = idx + dir;
    const list = [...(secNav.items || [])];
    if (target < 0 || target >= list.length) return;
    const [moved] = list.splice(idx, 1);
    list.splice(target, 0, moved);
    updateSecNav({ items: list });
    setExpandedItemIdx(target);
  };

  // Buscador de artículos para asignar a un botón directo (ej: "Kit Mudanza")
  const handleSearchArticlesForItem = async (q) => {
    setArticleSearchQuery(q);
    if (!q || q.trim().length < 2) {
      setArticleSearchResults([]);
      return;
    }
    setSearchingArticles(true);
    try {
      const res = await axios.get('/api/articulos/', {
        params: { search: q.trim(), page: 1, page_size: 12, company: selectedCompanyId },
        headers: selectedCompanyId ? { 'X-Company-ID': String(selectedCompanyId) } : {}
      });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setArticleSearchResults(data);
    } catch {
      setArticleSearchResults([]);
    } finally {
      setSearchingArticles(false);
    }
  };

  // Helpers para Sub-familias personalizadas dentro de un Rubro en Lista Desplegable de Rubros
  const handleToggleDbFamilia = (itemIdx, rubroCode, familCode, include) => {
    const item = secNav.items[itemIdx];
    const currentMap = { ...(item.excludedFamiliasByRubro || {}) };
    const prevExcluded = Array.isArray(currentMap[rubroCode]) ? currentMap[rubroCode].map(String) : [];
    const fStr = String(familCode).trim();
    currentMap[rubroCode] = include
      ? prevExcluded.filter(x => x !== fStr)
      : [...prevExcluded.filter(x => x !== fStr), fStr];
    updateItem(itemIdx, { excludedFamiliasByRubro: currentMap });
  };

  const handleAddCustomSubToRubro = (itemIdx, rubroCode, preset = null) => {
    const item = secNav.items[itemIdx];
    const currentMap = { ...(item.customSubItemsByRubro || {}) };
    const list = Array.isArray(currentMap[rubroCode]) ? [...currentMap[rubroCode]] : [];
    list.push(
      preset || {
        id: `csub-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        label: 'Nueva sub-familia',
        targetType: 'search',
        targetValue: '',
        enabled: true
      }
    );
    currentMap[rubroCode] = list;
    updateItem(itemIdx, { customSubItemsByRubro: currentMap });
  };

  const handleUpdateCustomSubInRubro = (itemIdx, rubroCode, subIdx, patch) => {
    const item = secNav.items[itemIdx];
    const currentMap = { ...(item.customSubItemsByRubro || {}) };
    const list = [...(currentMap[rubroCode] || [])];
    list[subIdx] = { ...list[subIdx], ...patch };
    currentMap[rubroCode] = list;
    updateItem(itemIdx, { customSubItemsByRubro: currentMap });
  };

  const handleRemoveCustomSubFromRubro = (itemIdx, rubroCode, subIdx) => {
    const item = secNav.items[itemIdx];
    const currentMap = { ...(item.customSubItemsByRubro || {}) };
    const list = (currentMap[rubroCode] || []).filter((_, i) => i !== subIdx);
    currentMap[rubroCode] = list;
    updateItem(itemIdx, { customSubItemsByRubro: currentMap });
  };

  // Helpers para Mega-Menú (Columnas y Sub-ítems)
  const handleAddColumn = (itemIdx) => {
    const item = secNav.items[itemIdx];
    const cols = Array.isArray(item.columns) ? [...item.columns] : [];
    cols.push({
      id: `col-${Date.now()}`,
      title: `Nueva Familia / Columna ${cols.length + 1}`,
      enabled: true,
      targetType: 'rubro',
      targetValue: '',
      showMoreLink: true,
      moreLinkText: 'Ver más',
      subItems: []
    });
    updateItem(itemIdx, { columns: cols });
  };

  const handleUpdateColumn = (itemIdx, colIdx, patch) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    cols[colIdx] = { ...cols[colIdx], ...patch };
    updateItem(itemIdx, { columns: cols });
  };

  const handleRemoveColumn = (itemIdx, colIdx) => {
    const item = secNav.items[itemIdx];
    const cols = (item.columns || []).filter((_, i) => i !== colIdx);
    updateItem(itemIdx, { columns: cols });
  };

  const handleMoveColumn = (itemIdx, colIdx, dir) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const target = colIdx + dir;
    if (target < 0 || target >= cols.length) return;
    const [moved] = cols.splice(colIdx, 1);
    cols.splice(target, 0, moved);
    updateItem(itemIdx, { columns: cols });
  };

  const handleAddSubItem = (itemIdx, colIdx, preset = null) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const col = cols[colIdx];
    const subs = Array.isArray(col.subItems) ? [...col.subItems] : [];
    subs.push(
      preset || {
        id: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        label: 'Nueva sub-familia',
        targetType: 'search',
        targetValue: '',
        enabled: true
      }
    );
    cols[colIdx] = { ...col, subItems: subs };
    updateItem(itemIdx, { columns: cols });
  };

  const handleImportRubroFamiliasToColumn = (itemIdx, colIdx, rubroCode) => {
    const foundRubro = rubrosWithFamilias.find(r => String(r.c_rubro).trim() === String(rubroCode).trim());
    if (!foundRubro || !Array.isArray(foundRubro.familias) || foundRubro.familias.length === 0) return;
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const col = cols[colIdx];
    const currentSubs = Array.isArray(col.subItems) ? [...col.subItems] : [];
    foundRubro.familias.forEach(fm => {
      const fVal = `${foundRubro.c_rubro_clean}|${fm.c_famil}`;
      if (!currentSubs.some(s => s.targetValue === fVal || s.label === fm.d_famil)) {
        currentSubs.push({
          id: `sub-fam-${fm.c_famil}-${Date.now()}`,
          label: fm.d_famil,
          targetType: 'familia',
          targetValue: fVal,
          enabled: true
        });
      }
    });
    cols[colIdx] = {
      ...col,
      title: col.title && !col.title.startsWith('Nueva ') && !col.title.startsWith('Sub-sección') ? col.title : foundRubro.d_rubro,
      targetType: 'rubro',
      targetValue: foundRubro.c_rubro_clean,
      subItems: currentSubs
    };
    updateItem(itemIdx, { columns: cols });
  };

  const handleUpdateSubItem = (itemIdx, colIdx, subIdx, patch) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const col = cols[colIdx];
    const subs = [...(col.subItems || [])];
    subs[subIdx] = { ...subs[subIdx], ...patch };
    cols[colIdx] = { ...col, subItems: subs };
    updateItem(itemIdx, { columns: cols });
  };

  const handleRemoveSubItem = (itemIdx, colIdx, subIdx) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const col = cols[colIdx];
    const subs = (col.subItems || []).filter((_, i) => i !== subIdx);
    cols[colIdx] = { ...col, subItems: subs };
    updateItem(itemIdx, { columns: cols });
  };

  const handleMoveSubItem = (itemIdx, colIdx, subIdx, dir) => {
    const item = secNav.items[itemIdx];
    const cols = [...(item.columns || [])];
    const col = cols[colIdx];
    const subs = [...(col.subItems || [])];
    const target = subIdx + dir;
    if (target < 0 || target >= subs.length) return;
    const [moved] = subs.splice(subIdx, 1);
    subs.splice(target, 0, moved);
    cols[colIdx] = { ...col, subItems: subs };
    updateItem(itemIdx, { columns: cols });
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden mb-4">
      <div className="card-header bg-white py-3 px-4 border-bottom d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-3">
          <div
            className="bg-info-subtle text-info-emphasis rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '42px', height: '42px' }}
          >
            <i className="bi bi-menu-app-fill fs-5"></i>
          </div>
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <h5 className="fw-bold text-dark mb-0">Barra Secundaria de Categorías & Mega-Menú Comercial</h5>
              <span className={`badge rounded-pill px-3 py-1 ${secNav.enabled ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                {secNav.enabled ? 'Activa en el Sitio' : 'Desactivada'}
              </span>
            </div>
            <small className="text-muted d-block">
              Barra horizontal debajo del encabezado principal con accesos directos (ej. "¡Los Más Vendidos!", "Kit", "Preguntas frecuentes") y menús desplegables de rubros, familias y sub-familias.
            </small>
          </div>
        </div>

        <div className="form-check form-switch d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-pill border mb-0 flex-shrink-0">
          <input
            className="form-check-input ms-0 cursor-pointer"
            type="checkbox"
            role="switch"
            id="enableSecondaryNavBarSwitch"
            checked={Boolean(secNav.enabled)}
            onChange={(e) => updateSecNav({ enabled: e.target.checked })}
            style={{ transform: 'scale(1.15)' }}
          />
          <label className="form-check-label small fw-bold text-dark cursor-pointer mb-0 ms-2" htmlFor="enableSecondaryNavBarSwitch">
            {secNav.enabled ? 'Barra Visible' : 'Barra Oculta'}
          </label>
        </div>
      </div>

      <div className="card-body p-4">
        {/* 1. Ajustes de Apariencia y Visibilidad de la Barra */}
        <div className="p-3 p-md-4 bg-light rounded-4 border mb-4">
          <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
            <i className="bi bi-sliders text-primary"></i>
            <span>Diseño, Colores y Ubicación de la Barra</span>
          </h6>

          <div className="row g-3">
            {/* Dónde mostrar */}
            <div className="col-12 col-md-4">
              <label className="form-label small fw-bold text-dark mb-1">¿Dónde mostrar la barra?</label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={secNav.visibilityScope || 'all'}
                onChange={(e) => updateSecNav({ visibilityScope: e.target.value })}
              >
                <option value="all">En todo el sitio (Portada + Tienda + Páginas)</option>
                <option value="tienda">Solo en la Tienda Online (/tienda)</option>
                <option value="home">Solo en la Portada de Inicio (/)</option>
              </select>
            </div>

            {/* Color de Fondo de la Barra */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-dark mb-1">Color de Barra</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color border rounded-3 p-1 cursor-pointer flex-shrink-0"
                  style={{ width: '36px', height: '32px' }}
                  value={secNav.backgroundColor || '#bae6fd'}
                  onChange={(e) => updateSecNav({ backgroundColor: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control form-control-sm font-monospace"
                  value={secNav.backgroundColor || '#bae6fd'}
                  onChange={(e) => updateSecNav({ backgroundColor: e.target.value })}
                />
              </div>
            </div>

            {/* Color de Texto de la Barra */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-dark mb-1">Color de Texto</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color border rounded-3 p-1 cursor-pointer flex-shrink-0"
                  style={{ width: '36px', height: '32px' }}
                  value={secNav.textColor || '#0f172a'}
                  onChange={(e) => updateSecNav({ textColor: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control form-control-sm font-monospace"
                  value={secNav.textColor || '#0f172a'}
                  onChange={(e) => updateSecNav({ textColor: e.target.value })}
                />
              </div>
            </div>

            {/* Tipografía de la Barra */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-dark mb-1">
                <i className="bi bi-fonts me-1 text-primary"></i> Tipografía Barra
              </label>
              <select
                className="form-select form-select-sm rounded-3 fw-semibold"
                value={secNav.fontFamily || 'inherit'}
                onChange={(e) => updateSecNav({ fontFamily: e.target.value })}
              >
                <option value="inherit">Heredar del sitio</option>
                {AVAILABLE_FONTS.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            {/* Tamaño y Peso de Fuente */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-dark mb-1">Tamaño y Grosor</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm rounded-3"
                  value={secNav.fontSize || 'md'}
                  onChange={(e) => updateSecNav({ fontSize: e.target.value })}
                >
                  <option value="sm">Chico</option>
                  <option value="md">Mediano</option>
                  <option value="lg">Grande</option>
                </select>
                <select
                  className="form-select form-select-sm rounded-3"
                  value={secNav.fontWeight || 'semibold'}
                  onChange={(e) => updateSecNav({ fontWeight: e.target.value })}
                >
                  <option value="normal">Normal</option>
                  <option value="medium">Medio</option>
                  <option value="semibold">Semi-Negrita</option>
                  <option value="bold">Negrita</option>
                  <option value="extrabold">Extra-Negrita</option>
                </select>
              </div>
            </div>

            {/* Alineación */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-dark mb-1">Alineación</label>
              <select
                className="form-select form-select-sm rounded-3 fw-semibold"
                value={secNav.alignment || 'center'}
                onChange={(e) => updateSecNav({ alignment: e.target.value })}
              >
                <option value="center">Centrado</option>
                <option value="start">Izquierda</option>
                <option value="between">Distribuido</option>
              </select>
            </div>
          </div>

          {/* Vista previa rápida de la barra */}
          <div className="mt-3 pt-3 border-top">
            <span className="small text-muted fw-semibold d-block mb-2">Vista previa de la barra:</span>
            <div
              className="rounded-3 px-3 py-2 d-flex align-items-center flex-wrap gap-3 border shadow-2xs"
              style={{
                backgroundColor: secNav.backgroundColor || '#bae6fd',
                color: secNav.textColor || '#0f172a',
                fontFamily: getFontFamilyCss(secNav.fontFamily) || 'inherit',
                justifyContent:
                  secNav.alignment === 'start'
                    ? 'flex-start'
                    : secNav.alignment === 'between'
                    ? 'space-between'
                    : 'center'
              }}
            >
              {(secNav.items || [])
                .filter(i => i && i.enabled !== false)
                .map((it, i) => (
                  <span
                    key={it.id || i}
                    className="d-inline-flex align-items-center gap-2"
                    style={{
                      fontWeight: it.highlight ? 800 : 600,
                      fontSize: secNav.fontSize === 'lg' ? '0.95rem' : secNav.fontSize === 'sm' ? '0.78rem' : '0.86rem',
                      color: it.customTextColor || secNav.textColor || '#0f172a'
                    }}
                  >
                    {it.badgeText && (
                      <span
                        className="badge rounded-pill px-2 py-1"
                        style={{ backgroundColor: it.badgeColor || '#ef4444', color: '#fff', fontSize: '0.64rem' }}
                      >
                        {it.badgeText}
                      </span>
                    )}
                    <span>{it.label || 'Sin título'}</span>
                    {(it.type === 'rubros_dropdown' || it.type === 'megamenu') && (
                      <i className="bi bi-chevron-down" style={{ fontSize: '0.7rem' }}></i>
                    )}
                  </span>
                ))}
            </div>
          </div>
        </div>

        {/* 2. Botonera para Agregar Ítems a la Barra */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
          <div>
            <h6 className="fw-bold text-dark mb-1">Botones y Menús Desplegables de la Barra ({(secNav.items || []).length})</h6>
            <small className="text-muted">
              Agregá accesos directos (a un rubro, búsqueda, kit, más vendidos o preguntas frecuentes), listas desplegables de rubros/familias o mega-menús con sub-secciones.
            </small>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill px-3 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow-xs"
              onClick={() => handleAddItem('direct')}
            >
              <i className="bi bi-plus-circle-fill"></i>
              <span>Acceso Directo / Botón</span>
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-pill px-3 py-2 fw-bold d-inline-flex align-items-center gap-2"
              onClick={() => handleAddItem('rubros_dropdown')}
            >
              <i className="bi bi-list-ul"></i>
              <span>Lista Desplegable de Rubros y Familias</span>
            </button>
            <button
              type="button"
              className="btn btn-dark btn-sm rounded-pill px-3 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow-xs"
              onClick={() => handleAddItem('megamenu')}
            >
              <i className="bi bi-columns-gap"></i>
              <span>Mega-Menú con Sub-secciones</span>
            </button>
          </div>
        </div>

        {/* 3. Lista de Ítems Configurados */}
        <div className="d-flex flex-column gap-3">
          {(secNav.items || []).map((item, idx) => {
            const isExpanded = expandedItemIdx === idx;
            const typeBadge =
              item.type === 'megamenu'
                ? { text: 'Mega-Menú (Sub-secciones ▾)', cls: 'bg-dark text-white' }
                : item.type === 'rubros_dropdown'
                ? { text: 'Rubros y Familias ▾', cls: 'bg-info-subtle text-info-emphasis border border-info-subtle' }
                : { text: 'Acceso Directo', cls: 'bg-primary-subtle text-primary border border-primary-subtle' };

            return (
              <div
                key={item.id || idx}
                className={`rounded-4 border transition-all ${
                  item.enabled !== false ? 'bg-white border-secondary-subtle shadow-2xs' : 'bg-light opacity-75'
                }`}
              >
                {/* Cabecera del Ítem */}
                <div className="p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                    <div className="btn-group btn-group-sm me-1">
                      <button
                        type="button"
                        className="btn btn-light border px-2 py-1"
                        disabled={idx === 0}
                        onClick={() => handleMoveItem(idx, -1)}
                        title="Mover a la izquierda / arriba"
                      >
                        <i className="bi bi-chevron-up"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-light border px-2 py-1"
                        disabled={idx === (secNav.items || []).length - 1}
                        onClick={() => handleMoveItem(idx, 1)}
                        title="Mover a la derecha / abajo"
                      >
                        <i className="bi bi-chevron-down"></i>
                      </button>
                    </div>

                    <span className="badge bg-light text-secondary border font-monospace px-2 py-1">#{idx + 1}</span>
                    <span className={`badge rounded-pill px-3 py-1 ${typeBadge.cls}`} style={{ fontSize: '0.73rem' }}>
                      {typeBadge.text}
                    </span>
                    <strong className="text-dark text-truncate ms-1">{item.label || 'Sin nombre'}</strong>
                    {item.highlight && (
                      <span className="badge bg-warning-subtle text-dark border border-warning-subtle rounded-pill px-2 py-1" style={{ fontSize: '0.68rem' }}>
                        Resaltado
                      </span>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-2 ${
                        isExpanded ? 'btn-primary text-white' : 'btn-outline-secondary'
                      }`}
                      onClick={() => setExpandedItemIdx(isExpanded ? null : idx)}
                    >
                      <i className={`bi bi-${isExpanded ? 'chevron-up' : 'pencil-square'}`}></i>
                      <span>{isExpanded ? 'Cerrar edición' : 'Configurar'}</span>
                    </button>

                    <div className="form-check form-switch mb-0" title="Activar o desactivar este botón">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        checked={item.enabled !== false}
                        onChange={(e) => updateItem(idx, { enabled: e.target.checked })}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: '32px', height: '32px', padding: 0 }}
                      onClick={() => handleRemoveItem(idx)}
                      title="Eliminar ítem de la barra"
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  </div>
                </div>

                {/* Cuerpo Desplegable de Configuración del Ítem */}
                {isExpanded && (
                  <div className="p-3 p-md-4 border-top bg-light bg-opacity-50">
                    <div className="row g-3 mb-3">
                      {/* Texto del botón */}
                      <div className="col-12 col-md-4">
                        <label className="form-label small fw-bold text-dark mb-1">Texto visible en la barra</label>
                        <input
                          type="text"
                          className="form-control form-control-sm fw-bold rounded-3"
                          value={item.label || ''}
                          onChange={(e) => updateItem(idx, { label: e.target.value })}
                          placeholder="Ej: ¡Los Más Vendidos!, Rubros Destacados, Kit..."
                        />
                      </div>

                      {/* Tipo de Comportamiento */}
                      <div className="col-12 col-md-4">
                        <label className="form-label small fw-bold text-dark mb-1">¿Qué hace al tocarlo?</label>
                        <select
                          className="form-select form-select-sm fw-semibold rounded-3"
                          value={item.type || 'direct'}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const patch = { type: newType };
                            if (newType === 'megamenu') {
                              if (!Array.isArray(item.columns) || item.columns.length === 0) {
                                patch.columns = [
                                  {
                                    id: `col-${Date.now()}`,
                                    title: 'Familia / Sub-sección 1',
                                    enabled: true,
                                    targetType: 'rubro',
                                    targetValue: '',
                                    showMoreLink: true,
                                    moreLinkText: 'Ver más',
                                    subItems: []
                                  }
                                ];
                              }
                              if (!item.fontFamily) patch.fontFamily = 'inherit';
                              if (!item.lineHeight) patch.lineHeight = '1.45';
                              if (!item.itemSpacing) patch.itemSpacing = '8px';
                            } else if (newType === 'rubros_dropdown') {
                              if (!item.fontFamily) patch.fontFamily = 'inherit';
                              if (!item.lineHeight) patch.lineHeight = '1.45';
                              if (!item.itemSpacing) patch.itemSpacing = '8px';
                            }
                            updateItem(idx, patch);
                          }}
                        >
                          <option value="direct">Acceso Directo (Redirige con 1 clic)</option>
                          <option value="rubros_dropdown">Lista Desplegable de Rubros y Familias (▾)</option>
                          <option value="megamenu">Mega-Menú con Familias / Columnas a medida (▾)</option>
                        </select>
                      </div>

                      {/* Resaltado y Etiqueta (Badge) */}
                      <div className="col-12 col-md-4">
                        <label className="form-label small fw-bold text-dark mb-1">Destacado / Etiqueta opcional</label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-check form-switch mb-0 bg-white border rounded-3 px-3 py-1 d-inline-flex align-items-center gap-2 flex-shrink-0">
                            <input
                              className="form-check-input ms-0 cursor-pointer"
                              type="checkbox"
                              role="switch"
                              id={`hi-sec-${idx}`}
                              checked={Boolean(item.highlight)}
                              onChange={(e) => updateItem(idx, { highlight: e.target.checked })}
                            />
                            <label className="form-check-label small fw-semibold cursor-pointer mb-0 ms-1" htmlFor={`hi-sec-${idx}`}>
                              Negrita Extra
                            </label>
                          </div>
                          <input
                            type="text"
                            className="form-control form-control-sm rounded-3"
                            placeholder="Badge (ej: 🔥, OFERTA)"
                            value={item.badgeText || ''}
                            onChange={(e) => updateItem(idx, { badgeText: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* CONFIGURACIÓN SEGÚN EL TIPO DE ÍTEM */}

                    {/* MODO 1: ACCESO DIRECTO */}
                    {item.type === 'direct' && (
                      <div className="p-3 bg-white rounded-3 border">
                        <div className="row g-3 align-items-start">
                          <div className="col-12 col-md-4">
                            <label className="form-label small fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                              <i className="bi bi-cursor-fill"></i>
                              <span>Destino del Acceso Directo</span>
                            </label>
                            <select
                              className="form-select form-select-sm fw-semibold rounded-3"
                              value={item.targetType || 'search'}
                              onChange={(e) => updateItem(idx, { targetType: e.target.value })}
                            >
                              <option value="mas_vendidos">🔥 Sección / Filtro "¡Los Más Vendidos!"</option>
                              <option value="rubro">📂 Un Rubro específico de la Tienda</option>
                              <option value="search">🔍 Palabra clave o filtro (ej: "Kit Mudanza", "Cyber")</option>
                              <option value="article">📦 Uno o varios Artículos / Kit específicos</option>
                              <option value="ofertas">🏷️ Artículos en Oferta / Promoción</option>
                              <option value="faq">❓ Página de Preguntas Frecuentes (/preguntas-frecuentes)</option>
                              <option value="url">🔗 Otra página interna o URL personalizada</option>
                            </select>
                          </div>

                          <div className="col-12 col-md-8">
                            {item.targetType === 'mas_vendidos' && (
                              <div className="p-3 bg-warning-subtle border border-warning-subtle text-dark rounded-3 small d-flex align-items-center gap-2">
                                <i className="bi bi-fire text-danger fs-5 flex-shrink-0"></i>
                                <span>
                                  Al hacer clic, llevará al cliente a los productos configurados en <strong>"¡Los Más Vendidos!"</strong> (que podés elegir en la pestaña 4).
                                </span>
                              </div>
                            )}

                            {item.targetType === 'ofertas' && (
                              <div className="p-3 bg-danger-subtle border border-danger-subtle text-danger-emphasis rounded-3 small d-flex align-items-center gap-2">
                                <i className="bi bi-tag-fill fs-5 flex-shrink-0"></i>
                                <span>Al hacer clic, mostrará todos los artículos en oferta en la Tienda Online.</span>
                              </div>
                            )}

                            {item.targetType === 'faq' && (
                              <div className="p-3 bg-info-subtle border border-info-subtle text-info-emphasis rounded-3 small d-flex align-items-center gap-2">
                                <i className="bi bi-question-circle-fill fs-5 flex-shrink-0"></i>
                                <span>
                                  Al hacer clic, abrirá la sección de <strong>Preguntas Frecuentes</strong> (<code>/preguntas-frecuentes</code>).
                                </span>
                              </div>
                            )}

                            {item.targetType === 'rubro' && (
                              <div>
                                <label className="form-label small fw-bold text-dark mb-1">Elegí el Rubro destino:</label>
                                <select
                                  className="form-select form-select-sm rounded-3"
                                  value={item.targetValue || ''}
                                  onChange={(e) => updateItem(idx, { targetValue: e.target.value })}
                                >
                                  <option value="">-- Seleccionar Rubro --</option>
                                  {rubrosWithFamilias.map(r => (
                                    <option key={r.c_rubro} value={r.c_rubro}>
                                      {r.d_rubro} (Cód: {r.c_rubro})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {item.targetType === 'search' && (
                              <div>
                                <label className="form-label small fw-bold text-dark mb-1">
                                  Texto o palabra a filtrar en la tienda (ej: "Kit Mudanza", "Cinta", "Combo"):
                                </label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm rounded-3"
                                  value={item.targetValue || ''}
                                  onChange={(e) => updateItem(idx, { targetValue: e.target.value })}
                                  placeholder={`Si lo dejás vacío buscará "${item.label || ''}"`}
                                />
                              </div>
                            )}

                            {item.targetType === 'url' && (
                              <div>
                                <label className="form-label small fw-bold text-dark mb-1">Ruta o enlace URL:</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm font-monospace rounded-3"
                                  value={item.targetValue || ''}
                                  onChange={(e) => updateItem(idx, { targetValue: e.target.value })}
                                  placeholder="/novedades, /contacto, /descargas o https://..."
                                />
                              </div>
                            )}

                            {item.targetType === 'article' && (
                              <div>
                                <label className="form-label small fw-bold text-dark mb-1">
                                  Buscar y seleccionar el/los artículo(s) (ej: un Kit o selección puntual):
                                </label>
                                <div className="input-group input-group-sm mb-2">
                                  <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar por código o nombre de artículo..."
                                    value={articleSearchQuery}
                                    onChange={(e) => handleSearchArticlesForItem(e.target.value)}
                                  />
                                </div>
                                {searchingArticles && <div className="small text-muted mb-2">Buscando artículos...</div>}
                                {articleSearchResults.length > 0 && (
                                  <div className="list-group mb-2 border rounded-3" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                    {articleSearchResults.map(art => {
                                      const already = (item.selectedArticles || []).some(a => String(a.c_artic) === String(art.c_artic));
                                      return (
                                        <button
                                          key={art.c_artic}
                                          type="button"
                                          className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-2 px-3 small"
                                          onClick={() => {
                                            if (already) return;
                                            const nextArts = [...(item.selectedArticles || []), { c_artic: art.c_artic, d_artic: art.d_artic }];
                                            updateItem(idx, { selectedArticles: nextArts });
                                          }}
                                        >
                                          <span className="text-truncate">
                                            <code>{art.c_artic}</code> — <strong>{art.d_artic}</strong>
                                          </span>
                                          <span className={`badge rounded-pill px-2 py-1 ${already ? 'bg-success' : 'bg-primary'}`}>
                                            {already ? 'Agregado' : '+ Agregar'}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {(item.selectedArticles || []).length > 0 && (
                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                    {(item.selectedArticles || []).map((art, aIdx) => (
                                      <span key={art.c_artic || aIdx} className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2">
                                        <span>{art.c_artic} - {art.d_artic}</span>
                                        <i
                                          className="bi bi-x-circle-fill cursor-pointer text-danger"
                                          onClick={() => {
                                            const nextArts = (item.selectedArticles || []).filter((_, i) => i !== aIdx);
                                            updateItem(idx, { selectedArticles: nextArts });
                                          }}
                                        ></i>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MODO 2: LISTA DESPLEGABLE DE RUBROS Y SUS FAMILIAS / SUB-FAMILIAS */}
                    {item.type === 'rubros_dropdown' && (() => {
                      const activeRubrosList = item.rubrosMode === 'selected' && Array.isArray(item.selectedRubros) && item.selectedRubros.length > 0
                        ? rubrosWithFamilias.filter(r => item.selectedRubros.map(String).includes(String(r.c_rubro).trim()))
                        : rubrosWithFamilias;

                      return (
                        <div className="p-3 bg-white rounded-3 border">
                          {/* Tipografía, Interlineado y Espacio de Opciones de Rubros */}
                          <div className="p-3 bg-light rounded-4 border mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <h6 className="fw-bold small text-dark mb-0 d-flex align-items-center gap-2">
                                <i className="bi bi-fonts text-primary fs-6"></i>
                                <span>Tipografía, Interlineado y Espacio entre Opciones (Desplegable de Rubros)</span>
                              </h6>
                              <span className="badge bg-white text-muted border fw-normal" style={{ fontSize: '0.72rem' }}>
                                Personalización Visual
                              </span>
                            </div>
                            <div className="row g-2">
                              <div className="col-12 col-sm-4">
                                <label className="form-label small fw-semibold text-secondary mb-1">Tipo de Letra</label>
                                <select
                                  className="form-select form-select-sm rounded-3 fw-semibold"
                                  value={item.fontFamily || 'inherit'}
                                  onChange={(e) => updateItem(idx, { fontFamily: e.target.value })}
                                >
                                  <option value="inherit">Heredar del sitio (Principal)</option>
                                  {AVAILABLE_FONTS.map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="col-6 col-sm-4">
                                <label className="form-label small fw-semibold text-secondary mb-1">Interlineado</label>
                                <select
                                  className="form-select form-select-sm rounded-3 fw-semibold"
                                  value={item.lineHeight || '1.45'}
                                  onChange={(e) => updateItem(idx, { lineHeight: e.target.value })}
                                >
                                  <option value="1.15">Muy compacto (1.15)</option>
                                  <option value="1.30">Compacto (1.30)</option>
                                  <option value="1.45">Normal (1.45 - Estándar)</option>
                                  <option value="1.65">Holgado (1.65)</option>
                                  <option value="1.85">Amplio (1.85)</option>
                                </select>
                              </div>

                              <div className="col-6 col-sm-4">
                                <label className="form-label small fw-semibold text-secondary mb-1">Espacio entre Opciones</label>
                                <select
                                  className="form-select form-select-sm rounded-3 fw-semibold"
                                  value={item.itemSpacing || '8px'}
                                  onChange={(e) => updateItem(idx, { itemSpacing: e.target.value })}
                                >
                                  <option value="4px">Muy compacto (4px)</option>
                                  <option value="6px">Compacto (6px)</option>
                                  <option value="8px">Normal (8px - Estándar)</option>
                                  <option value="12px">Cómodo (12px)</option>
                                  <option value="16px">Espaciado (16px)</option>
                                  <option value="20px">Amplio (20px)</option>
                                  <option value="24px">Muy amplio (24px)</option>
                                </select>
                              </div>
                            </div>

                            {/* Vista previa en vivo */}
                            <div className="mt-2.5 pt-2 border-top d-flex align-items-center gap-3">
                              <span className="text-muted" style={{ fontSize: '0.73rem' }}>Vista previa del estilo:</span>
                              <div
                                className="d-inline-flex flex-column bg-white px-3 py-1.5 rounded-2 border"
                                style={{
                                  gap: item.itemSpacing || '8px',
                                  fontFamily: getFontFamilyCss(item.fontFamily) || 'inherit',
                                  lineHeight: item.lineHeight || '1.45',
                                  fontSize: '0.84rem'
                                }}
                              >
                                <span className="fw-bold text-dark">Ejemplo Rubro</span>
                                <span className="text-muted">Sub-familia opción 1</span>
                                <span className="text-muted">Sub-familia opción 2</span>
                              </div>
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-12 col-md-5">
                              <label className="form-label small fw-bold text-primary mb-1">
                                ¿Qué rubros debe desplegar esta lista?
                              </label>
                              <select
                                className="form-select form-select-sm fw-semibold rounded-3 mb-3"
                                value={item.rubrosMode || 'all'}
                                onChange={(e) => updateItem(idx, { rubrosMode: e.target.value })}
                              >
                                <option value="all">Mostrar TODOS los rubros automáticamente</option>
                                <option value="selected">Elegir algunos rubros específicos de la lista</option>
                              </select>

                              <label className="form-label small fw-bold text-dark mb-1">
                                Cantidad de columnas de familias al abrir el desplegable
                              </label>
                              <select
                                className="form-select form-select-sm rounded-3 mb-3"
                                value={item.columnsCount || 3}
                                onChange={(e) => updateItem(idx, { columnsCount: Number(e.target.value) })}
                              >
                                <option value={1}>1 Columna</option>
                                <option value={2}>2 Columnas</option>
                                <option value={3}>3 Columnas</option>
                                <option value={4}>4 Columnas</option>
                              </select>

                              <div className="form-check form-switch p-2 ps-5 bg-light rounded-3 border">
                                <input
                                  className="form-check-input cursor-pointer"
                                  type="checkbox"
                                  role="switch"
                                  id={`showDbFamSwitch-${idx}`}
                                  checked={item.showDbFamilias !== false}
                                  onChange={(e) => updateItem(idx, { showDbFamilias: e.target.checked })}
                                />
                                <label className="form-check-label small fw-bold text-dark cursor-pointer" htmlFor={`showDbFamSwitch-${idx}`}>
                                  Desplegar automáticamente las Familias / Sub-familias de cada Rubro
                                </label>
                                <small className="text-muted d-block mt-1" style={{ fontSize: '0.73rem' }}>
                                  Muestra el nombre del Rubro en negrita y debajo sus Familias.
                                </small>
                              </div>
                            </div>

                            <div className="col-12 col-md-7">
                              {item.rubrosMode === 'selected' ? (
                                <div>
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label className="form-label small fw-bold text-dark mb-0">
                                      Tildá los rubros que querés incluir ({(item.selectedRubros || []).length} seleccionados):
                                    </label>
                                    <div className="d-flex gap-3">
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                                        onClick={() => updateItem(idx, { selectedRubros: rubrosWithFamilias.map(r => String(r.c_rubro).trim()) })}
                                      >
                                        Marcar todos
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-danger text-decoration-none fw-semibold"
                                        onClick={() => updateItem(idx, { selectedRubros: [] })}
                                      >
                                        Limpiar
                                      </button>
                                    </div>
                                  </div>
                                  <div
                                    className="row g-2 p-2 bg-light rounded-3 border"
                                    style={{ maxHeight: '210px', overflowY: 'auto' }}
                                  >
                                    {rubrosWithFamilias.map(r => {
                                      const code = String(r.c_rubro).trim();
                                      const isChecked = (item.selectedRubros || []).map(String).includes(code);
                                      const famCount = (r.familias || []).length;
                                      return (
                                        <div key={code} className="col-12 col-sm-6">
                                          <label className="form-check d-flex align-items-center justify-content-between gap-2 small bg-white px-3 py-2 rounded-2 border cursor-pointer mb-0">
                                            <div className="d-flex align-items-center gap-2 min-w-0">
                                              <input
                                                type="checkbox"
                                                className="form-check-input mt-0 flex-shrink-0"
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  const current = (item.selectedRubros || []).map(String);
                                                  const next = e.target.checked
                                                    ? [...current, code]
                                                    : current.filter(c => c !== code);
                                                  updateItem(idx, { selectedRubros: next });
                                                }}
                                              />
                                              <span className="text-truncate fw-medium">{r.d_rubro}</span>
                                            </div>
                                            {famCount > 0 && (
                                              <span className="badge bg-info-subtle text-info-emphasis rounded-pill flex-shrink-0" style={{ fontSize: '0.66rem' }}>
                                                {famCount} fam.
                                              </span>
                                            )}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 bg-light rounded-3 border small text-muted h-100 d-flex align-items-center">
                                  <div className="d-flex align-items-start gap-2">
                                    <i className="bi bi-check2-all text-success fs-5 flex-shrink-0"></i>
                                    <div>
                                      <strong className="text-dark d-block mb-1">Todos los Rubros y sus Familias activos</strong>
                                      Se mostrarán automáticamente los {rubrosWithFamilias.length} rubros de la empresa. Aquellos que tengan Familias o Sub-familias se verán con el título en negrita y sus sub-familias debajo.
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* GESTIÓN DE FAMILIAS Y SUB-FAMILIAS DENTRO DE CADA RUBRO */}
                          <div className="pt-3 border-top">
                            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-2">
                              <div>
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                  <i className="bi bi-diagram-3-fill text-primary"></i>
                                  <span>Familias y Sub-familias dentro de cada Rubro</span>
                                </h6>
                                <small className="text-muted">
                                  Acá podés ver las familias que trae cada rubro de la base de datos o agregarle sub-familias manualmente para que se desplieguen al abrir el menú.
                                </small>
                              </div>
                            </div>

                            <div className="row g-2 mt-1" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                              {activeRubrosList.map(r => {
                                const cRub = r.c_rubro_clean;
                                const dbFams = Array.isArray(r.familias) ? r.familias : [];
                                const excluded = Array.isArray(item.excludedFamiliasByRubro?.[cRub])
                                  ? item.excludedFamiliasByRubro[cRub].map(String)
                                  : [];
                                const customSubs = Array.isArray(item.customSubItemsByRubro?.[cRub])
                                  ? item.customSubItemsByRubro[cRub]
                                  : [];
                                const activeDbCount = item.showDbFamilias !== false
                                  ? dbFams.filter(f => !excluded.includes(String(f.c_famil).trim())).length
                                  : 0;
                                const totalSubCount = activeDbCount + customSubs.length;
                                const isRubOpen = expandedRubroCustomCode === `${idx}-${cRub}`;

                                return (
                                  <div key={cRub} className="col-12">
                                    <div className="p-2 px-3 rounded-3 border bg-light">
                                      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                        <div className="d-flex align-items-center gap-2">
                                          <strong className="small text-dark">{r.d_rubro}</strong>
                                          <span className={`badge rounded-pill ${totalSubCount > 0 ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'}`} style={{ fontSize: '0.68rem' }}>
                                            {totalSubCount > 0 ? `${totalSubCount} sub-familias en el desplegable` : 'Acceso directo (sin sub-familias)'}
                                          </span>
                                        </div>

                                        <button
                                          type="button"
                                          className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1 ${
                                            isRubOpen ? 'btn-primary text-white' : 'btn-white bg-white border text-dark'
                                          }`}
                                          style={{ fontSize: '0.75rem' }}
                                          onClick={() => setExpandedRubroCustomCode(isRubOpen ? null : `${idx}-${cRub}`)}
                                        >
                                          <i className={`bi bi-${isRubOpen ? 'chevron-up' : 'sliders'}`}></i>
                                          <span>{isRubOpen ? 'Ocultar sub-familias' : 'Ver / Editar Sub-familias'}</span>
                                        </button>
                                      </div>

                                      {isRubOpen && (
                                        <div className="mt-3 pt-2 border-top">
                                          {/* Familias de la BD */}
                                          {dbFams.length > 0 && item.showDbFamilias !== false && (
                                            <div className="mb-3">
                                              <span className="small fw-bold text-secondary d-block mb-2">
                                                Familias del sistema para "{r.d_rubro}" (destildá las que no quieras mostrar):
                                              </span>
                                              <div className="d-flex flex-wrap gap-2">
                                                {dbFams.map(fm => {
                                                  const fCode = String(fm.c_famil).trim();
                                                  const isInc = !excluded.includes(fCode);
                                                  return (
                                                    <label
                                                      key={fCode}
                                                      className={`badge rounded-pill px-3 py-2 border d-inline-flex align-items-center gap-2 cursor-pointer ${
                                                        isInc ? 'bg-white text-dark border-primary' : 'bg-light text-muted opacity-50'
                                                      }`}
                                                      style={{ fontSize: '0.76rem', fontWeight: 500 }}
                                                    >
                                                      <input
                                                        type="checkbox"
                                                        className="form-check-input mt-0"
                                                        checked={isInc}
                                                        onChange={(e) => handleToggleDbFamilia(idx, cRub, fCode, e.target.checked)}
                                                      />
                                                      <span>{fm.d_famil}</span>
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}

                                          {/* Sub-familias personalizadas */}
                                          <div>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                              <span className="small fw-bold text-secondary">
                                                Sub-familias personalizadas debajo de "{r.d_rubro}" ({customSubs.length}):
                                              </span>
                                              <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => handleAddCustomSubToRubro(idx, cRub)}
                                              >
                                                <i className="bi bi-plus-circle"></i>
                                                <span>Agregar Sub-familia</span>
                                              </button>
                                            </div>

                                            {customSubs.length > 0 && (
                                              <div className="d-flex flex-column gap-2">
                                                {customSubs.map((cSub, csIdx) => (
                                                  <div key={cSub.id || csIdx} className="p-2 bg-white rounded-3 border d-flex flex-wrap align-items-center gap-2">
                                                    <input
                                                      type="text"
                                                      className="form-control form-control-sm flex-grow-1"
                                                      style={{ minWidth: '150px' }}
                                                      placeholder="Nombre de sub-familia (ej: Corrugado Simple)"
                                                      value={cSub.label || ''}
                                                      onChange={(e) => handleUpdateCustomSubInRubro(idx, cRub, csIdx, { label: e.target.value })}
                                                    />
                                                    <select
                                                      className="form-select form-select-sm"
                                                      style={{ width: 'auto', maxWidth: '160px' }}
                                                      value={cSub.targetType || 'search'}
                                                      onChange={(e) => handleUpdateCustomSubInRubro(idx, cRub, csIdx, { targetType: e.target.value })}
                                                    >
                                                      <option value="search">Búsqueda en Rubro</option>
                                                      <option value="rubro">Ir a otro Rubro</option>
                                                      <option value="url">Link URL</option>
                                                    </select>
                                                    {cSub.targetType === 'rubro' ? (
                                                      <select
                                                        className="form-select form-select-sm"
                                                        style={{ width: 'auto', maxWidth: '170px' }}
                                                        value={cSub.targetValue || ''}
                                                        onChange={(e) => handleUpdateCustomSubInRubro(idx, cRub, csIdx, { targetValue: e.target.value })}
                                                      >
                                                        <option value="">-- Elegir Rubro --</option>
                                                        {rubrosWithFamilias.map(rb => (
                                                          <option key={rb.c_rubro} value={rb.c_rubro}>{rb.d_rubro}</option>
                                                        ))}
                                                      </select>
                                                    ) : (
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        style={{ width: '160px' }}
                                                        placeholder={cSub.targetType === 'url' ? '/tienda?...' : 'Palabra filtro (opcional)'}
                                                        value={cSub.targetValue || ''}
                                                        onChange={(e) => handleUpdateCustomSubInRubro(idx, cRub, csIdx, { targetValue: e.target.value })}
                                                      />
                                                    )}
                                                    <button
                                                      type="button"
                                                      className="btn btn-outline-danger btn-sm px-2 py-1"
                                                      onClick={() => handleRemoveCustomSubFromRubro(idx, cRub, csIdx)}
                                                      title="Quitar sub-familia"
                                                    >
                                                      <i className="bi bi-trash3"></i>
                                                    </button>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* MODO 3: MEGA-MENÚ CON FAMILIAS Y SUB-SECCIONES A MEDIDA (COMO EN LA IMAGEN 2) */}
                    {item.type === 'megamenu' && (
                      <div className="p-3 bg-white rounded-3 border">
                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 mb-3 pb-2 border-bottom">
                          <div>
                            <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                              <i className="bi bi-columns-gap text-primary"></i>
                              <span>Familias y Sub-familias de "{item.label}"</span>
                            </h6>
                            <small className="text-muted">
                              Cada columna muestra el nombre de la Familia/Rubro en negrita (ej: "Cartón Corrugado", "Gastronomía") y debajo todas sus Sub-familias.
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm rounded-pill px-3 py-2 fw-bold d-inline-flex align-items-center gap-2 flex-shrink-0"
                            onClick={() => handleAddColumn(idx)}
                          >
                            <i className="bi bi-plus-lg"></i>
                            <span>Agregar Familia / Columna</span>
                          </button>
                        </div>

                        {/* Tipografía, Interlineado y Espacio de Opciones del Mega-Menú */}
                        <div className="p-3 bg-light rounded-4 border mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="fw-bold small text-dark mb-0 d-flex align-items-center gap-2">
                              <i className="bi bi-fonts text-primary fs-6"></i>
                              <span>Tipografía, Interlineado y Espacio entre Opciones (Mega-Menú)</span>
                            </h6>
                            <span className="badge bg-white text-muted border fw-normal" style={{ fontSize: '0.72rem' }}>
                              Personalización Visual
                            </span>
                          </div>
                          <div className="row g-2">
                            <div className="col-12 col-sm-4">
                              <label className="form-label small fw-semibold text-secondary mb-1">Tipo de Letra</label>
                              <select
                                className="form-select form-select-sm rounded-3 fw-semibold"
                                value={item.fontFamily || 'inherit'}
                                onChange={(e) => updateItem(idx, { fontFamily: e.target.value })}
                              >
                                <option value="inherit">Heredar del sitio (Principal)</option>
                                {AVAILABLE_FONTS.map(f => (
                                  <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="col-6 col-sm-4">
                              <label className="form-label small fw-semibold text-secondary mb-1">Interlineado</label>
                              <select
                                className="form-select form-select-sm rounded-3 fw-semibold"
                                value={item.lineHeight || '1.45'}
                                onChange={(e) => updateItem(idx, { lineHeight: e.target.value })}
                              >
                                <option value="1.15">Muy compacto (1.15)</option>
                                <option value="1.30">Compacto (1.30)</option>
                                <option value="1.45">Normal (1.45 - Estándar)</option>
                                <option value="1.65">Holgado (1.65)</option>
                                <option value="1.85">Amplio (1.85)</option>
                              </select>
                            </div>

                            <div className="col-6 col-sm-4">
                              <label className="form-label small fw-semibold text-secondary mb-1">Espacio entre Opciones</label>
                              <select
                                className="form-select form-select-sm rounded-3 fw-semibold"
                                value={item.itemSpacing || '8px'}
                                onChange={(e) => updateItem(idx, { itemSpacing: e.target.value })}
                              >
                                <option value="4px">Muy compacto (4px)</option>
                                <option value="6px">Compacto (6px)</option>
                                <option value="8px">Normal (8px - Estándar)</option>
                                <option value="12px">Cómodo (12px)</option>
                                <option value="16px">Espaciado (16px)</option>
                                <option value="20px">Amplio (20px)</option>
                                <option value="24px">Muy amplio (24px)</option>
                              </select>
                            </div>
                          </div>

                          {/* Vista previa en vivo */}
                          <div className="mt-2.5 pt-2 border-top d-flex align-items-center gap-3">
                            <span className="text-muted" style={{ fontSize: '0.73rem' }}>Vista previa del estilo:</span>
                            <div
                              className="d-inline-flex flex-column bg-white px-3 py-1.5 rounded-2 border"
                              style={{
                                gap: item.itemSpacing || '8px',
                                fontFamily: getFontFamilyCss(item.fontFamily) || 'inherit',
                                lineHeight: item.lineHeight || '1.45',
                                fontSize: '0.84rem'
                              }}
                            >
                              <span className="fw-bold text-dark">Nombre Columna</span>
                              <span className="text-muted">Sub-opción 1</span>
                              <span className="text-muted">Sub-opción 2</span>
                            </div>
                          </div>
                        </div>

                        <div className="row g-3">
                          {(item.columns || []).map((col, colIdx) => {
                            const selectedColRubro = rubrosWithFamilias.find(
                              r => String(r.c_rubro).trim() === String(col.targetValue || '').trim()
                            );
                            const availableDbFams = selectedColRubro?.familias || [];

                            return (
                              <div key={col.id || colIdx} className="col-12 col-xl-6">
                                <div className="p-3 rounded-4 border bg-light h-100 d-flex flex-column justify-content-between">
                                  <div>
                                    {/* Encabezado de la Columna */}
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="btn-group btn-group-sm">
                                          <button
                                            type="button"
                                            className="btn btn-white bg-white border px-2 py-1"
                                            disabled={colIdx === 0}
                                            onClick={() => handleMoveColumn(idx, colIdx, -1)}
                                          >
                                            <i className="bi bi-chevron-left"></i>
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-white bg-white border px-2 py-1"
                                            disabled={colIdx === (item.columns || []).length - 1}
                                            onClick={() => handleMoveColumn(idx, colIdx, 1)}
                                          >
                                            <i className="bi bi-chevron-right"></i>
                                          </button>
                                        </div>
                                        <span className="badge bg-dark text-white rounded-pill px-3 py-1">
                                          Familia / Columna #{colIdx + 1}
                                        </span>
                                      </div>

                                      <div className="d-flex align-items-center gap-3">
                                        <div className="form-check form-switch mb-0" title="Mostrar enlace 'Ver más' al pie de esta columna">
                                          <input
                                            className="form-check-input cursor-pointer"
                                            type="checkbox"
                                            role="switch"
                                            id={`col-more-${idx}-${colIdx}`}
                                            checked={col.showMoreLink !== false}
                                            onChange={(e) => handleUpdateColumn(idx, colIdx, { showMoreLink: e.target.checked })}
                                          />
                                          <label className="form-check-label small text-muted cursor-pointer ms-1" htmlFor={`col-more-${idx}-${colIdx}`}>
                                            "Ver más"
                                          </label>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm py-1 px-2 rounded-2"
                                          onClick={() => handleRemoveColumn(idx, colIdx)}
                                          title="Eliminar esta columna"
                                        >
                                          <i className="bi bi-trash3"></i>
                                        </button>
                                      </div>
                                    </div>

                                    {/* Título y Rubro Padre de la Columna */}
                                    <div className="row g-2 mb-3">
                                      <div className="col-12 col-sm-6">
                                        <label className="form-label small fw-bold text-dark mb-1">
                                          Título de la Familia (Negrita)
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm fw-bold rounded-3"
                                          value={col.title || ''}
                                          onChange={(e) => handleUpdateColumn(idx, colIdx, { title: e.target.value })}
                                          placeholder="Ej: Cartón Corrugado"
                                        />
                                      </div>
                                      <div className="col-12 col-sm-6">
                                        <label className="form-label small fw-bold text-muted mb-1">
                                          Vincular a Rubro del Sistema
                                        </label>
                                        <div className="d-flex gap-1">
                                          <select
                                            className="form-select form-select-sm rounded-3"
                                            value={col.targetValue || ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              const found = rubrosWithFamilias.find(r => String(r.c_rubro).trim() === String(val).trim());
                                              handleUpdateColumn(idx, colIdx, {
                                                targetType: 'rubro',
                                                targetValue: val,
                                                title: (!col.title || col.title.startsWith('Nueva ')) && found ? found.d_rubro : col.title
                                              });
                                            }}
                                          >
                                            <option value="">-- Rubro opcional --</option>
                                            {rubrosWithFamilias.map(r => (
                                              <option key={r.c_rubro} value={r.c_rubro}>
                                                {r.d_rubro} {r.familias?.length ? `(${r.familias.length} fam.)` : ''}
                                              </option>
                                            ))}
                                          </select>
                                          {availableDbFams.length > 0 && (
                                            <button
                                              type="button"
                                              className="btn btn-info btn-sm text-nowrap fw-semibold px-2"
                                              title="Traer automáticamente todas las familias de este rubro"
                                              onClick={() => handleImportRubroFamiliasToColumn(idx, colIdx, col.targetValue)}
                                            >
                                              <i className="bi bi-lightning-fill"></i> Traer Familias
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Sub-ítems de la columna */}
                                    <div className="mb-2">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="small fw-bold text-secondary">
                                          Sub-familias debajo de "{col.title || `Columna #${colIdx + 1}`}" ({(col.subItems || []).length})
                                        </span>
                                      </div>

                                      <div className="d-flex flex-column gap-2 mb-2">
                                        {(col.subItems || []).map((sub, subIdx) => (
                                          <div key={sub.id || subIdx} className="p-2 bg-white rounded-3 border d-flex flex-wrap align-items-center gap-2">
                                            <div className="btn-group btn-group-sm">
                                              <button
                                                type="button"
                                                className="btn btn-light border px-1 py-0"
                                                disabled={subIdx === 0}
                                                onClick={() => handleMoveSubItem(idx, colIdx, subIdx, -1)}
                                              >
                                                <i className="bi bi-arrow-up-short"></i>
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-light border px-1 py-0"
                                                disabled={subIdx === (col.subItems || []).length - 1}
                                                onClick={() => handleMoveSubItem(idx, colIdx, subIdx, 1)}
                                              >
                                                <i className="bi bi-arrow-down-short"></i>
                                              </button>
                                            </div>

                                            <input
                                              type="text"
                                              className="form-control form-control-sm flex-grow-1"
                                              style={{ minWidth: '130px' }}
                                              placeholder="Ej: Corrugado Simple"
                                              value={sub.label || ''}
                                              onChange={(e) => handleUpdateSubItem(idx, colIdx, subIdx, { label: e.target.value })}
                                            />

                                            <select
                                              className="form-select form-select-sm"
                                              style={{ width: 'auto', maxWidth: '135px' }}
                                              value={sub.targetType || 'search'}
                                              onChange={(e) => handleUpdateSubItem(idx, colIdx, subIdx, { targetType: e.target.value })}
                                            >
                                              <option value="familia">Familia BD</option>
                                              <option value="rubro">Rubro</option>
                                              <option value="search">Búsqueda</option>
                                              <option value="url">Link URL</option>
                                            </select>

                                            {sub.targetType === 'familia' ? (
                                              <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto', maxWidth: '175px' }}
                                                value={typeof sub.targetValue === 'string' ? sub.targetValue : ''}
                                                onChange={(e) => handleUpdateSubItem(idx, colIdx, subIdx, { targetValue: e.target.value })}
                                              >
                                                <option value="">-- Elegir Familia --</option>
                                                {rubrosWithFamilias.flatMap(r =>
                                                  (r.familias || []).map(fm => (
                                                    <option key={`${r.c_rubro_clean}|${fm.c_famil}`} value={`${r.c_rubro_clean}|${fm.c_famil}`}>
                                                      {r.d_rubro} → {fm.d_famil}
                                                    </option>
                                                  ))
                                                )}
                                              </select>
                                            ) : sub.targetType === 'rubro' ? (
                                              <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto', maxWidth: '165px' }}
                                                value={sub.targetValue || ''}
                                                onChange={(e) => handleUpdateSubItem(idx, colIdx, subIdx, { targetValue: e.target.value })}
                                              >
                                                <option value="">-- Elegir Rubro --</option>
                                                {rubrosWithFamilias.map(r => (
                                                  <option key={r.c_rubro} value={r.c_rubro}>{r.d_rubro}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                style={{ width: '140px' }}
                                                placeholder={sub.targetType === 'url' ? '/tienda?...' : 'Filtro (opcional)'}
                                                value={sub.targetValue || ''}
                                                onChange={(e) => handleUpdateSubItem(idx, colIdx, subIdx, { targetValue: e.target.value })}
                                              />
                                            )}

                                            <button
                                              type="button"
                                              className="btn btn-outline-danger btn-sm py-1 px-2"
                                              onClick={() => handleRemoveSubItem(idx, colIdx, subIdx)}
                                              title="Quitar sub-familia"
                                            >
                                              <i className="bi bi-x-lg"></i>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Botones para sumar sub-opción o sumar un rubro/familia rápido */}
                                  <div className="d-flex flex-wrap align-items-center gap-2 pt-2 border-top">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                      onClick={() => handleAddSubItem(idx, colIdx)}
                                    >
                                      <i className="bi bi-plus-circle"></i>
                                      <span>Agregar sub-familia</span>
                                    </button>

                                    {rubrosWithFamilias.length > 0 && (
                                      <select
                                        className="form-select form-select-sm rounded-pill"
                                        style={{ width: 'auto', maxWidth: '210px' }}
                                        value=""
                                        onChange={(e) => {
                                          const code = e.target.value;
                                          if (!code) return;
                                          const found = rubrosWithFamilias.find(r => String(r.c_rubro) === String(code));
                                          if (found) {
                                            handleAddSubItem(idx, colIdx, {
                                              id: `sub-${Date.now()}`,
                                              label: found.d_rubro,
                                              targetType: 'rubro',
                                              targetValue: found.c_rubro,
                                              enabled: true
                                            });
                                          }
                                        }}
                                      >
                                        <option value="">+ Sumar un Rubro rápido...</option>
                                        {rubrosWithFamilias.map(r => (
                                          <option key={r.c_rubro} value={r.c_rubro}>{r.d_rubro}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * 2. TARJETA ADMINISTRADORA DE "¡LOS MÁS VENDIDOS!"
 */
export const BestSellersAdminCard = ({
  config,
  setConfig,
  rubros = [],
  selectedCompanyId,
  onToggleHomeSection,
  isHomeSectionEnabled
}) => {
  const bs = {
    enabled: true,
    badgeText: '🔥 Ranking de Ventas',
    title: '¡Los Más Vendidos!',
    subtitle: 'Los productos más elegidos por nuestros clientes mayoristas y minoristas',
    selectionMode: 'manual',
    selectedProducts: [],
    selectedRubros: [],
    limit: 8,
    columnsPerRow: 4,
    ...(config.bestSellersSection || {})
  };

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const updateBs = (patch) => {
    setConfig(prev => ({
      ...prev,
      bestSellersSection: {
        ...bs,
        ...(prev.bestSellersSection || {}),
        ...patch
      }
    }));
  };

  const handleSearch = async (val) => {
    setQuery(val);
    if (!val || val.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get('/api/articulos/', {
        params: { search: val.trim(), page: 1, page_size: 15, company: selectedCompanyId },
        headers: selectedCompanyId ? { 'X-Company-ID': String(selectedCompanyId) } : {}
      });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden mb-4">
      <div className="card-header bg-white py-3 px-4 border-bottom d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-3">
          <div
            className="bg-danger-subtle text-danger rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '42px', height: '42px' }}
          >
            <i className="bi bi-fire fs-5"></i>
          </div>
          <div>
            <h5 className="fw-bold text-dark mb-0">Sección y Filtro "¡Los Más Vendidos!"</h5>
            <small className="text-muted">
              Elegí qué artículos o rubros componen "Los Más Vendidos" cuando el cliente entra desde la Barra Secundaria o desde el bloque en Portada.
            </small>
          </div>
        </div>

        <div className="form-check form-switch d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-pill border mb-0 flex-shrink-0">
          <input
            className="form-check-input ms-0 cursor-pointer"
            type="checkbox"
            role="switch"
            id="showBestSellersOnHomeSwitch"
            checked={Boolean(isHomeSectionEnabled)}
            onChange={(e) => {
              updateBs({ enabled: e.target.checked });
              if (onToggleHomeSection) onToggleHomeSection('bestSellers', e.target.checked);
            }}
          />
          <label className="form-check-label small fw-bold text-dark cursor-pointer mb-0 ms-1" htmlFor="showBestSellersOnHomeSwitch">
            {isHomeSectionEnabled ? 'Visible como Bloque en Portada' : 'Oculto en Portada (Activo como filtro)'}
          </label>
        </div>
      </div>

      <div className="card-body p-4">
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <label className="form-label small fw-bold text-dark mb-1">Título de la Sección</label>
            <input
              type="text"
              className="form-control form-control-sm fw-bold rounded-3"
              value={bs.title || ''}
              onChange={(e) => updateBs({ title: e.target.value })}
              placeholder="¡Los Más Vendidos!"
            />
          </div>
          <div className="col-12 col-md-5">
            <label className="form-label small fw-bold text-dark mb-1">Subtítulo</label>
            <input
              type="text"
              className="form-control form-control-sm rounded-3"
              value={bs.subtitle || ''}
              onChange={(e) => updateBs({ subtitle: e.target.value })}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-bold text-dark mb-1">Etiqueta Superior (Badge)</label>
            <input
              type="text"
              className="form-control form-control-sm rounded-3"
              value={bs.badgeText || ''}
              onChange={(e) => updateBs({ badgeText: e.target.value })}
            />
          </div>
        </div>

        <div className="row g-3 align-items-center mb-3">
          <div className="col-12 col-md-6">
            <label className="form-label small fw-bold text-primary mb-1">
              ¿Qué productos mostrar en "¡Los Más Vendidos!"?
            </label>
            <select
              className="form-select form-select-sm fw-semibold rounded-3"
              value={bs.selectionMode || 'manual'}
              onChange={(e) => updateBs({ selectionMode: e.target.value })}
            >
              <option value="manual">Selección Manual de Artículos (Elegir uno por uno)</option>
              <option value="rubros">Por Rubros Seleccionados (Uno o varios rubros)</option>
              <option value="featured">Usar los mismos de "Artículos Destacados"</option>
              <option value="ofertas">Mostrar Artículos en Oferta</option>
            </select>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label small fw-bold text-dark mb-1">Cantidad en Portada</label>
            <select
              className="form-select form-select-sm rounded-3"
              value={bs.limit || 8}
              onChange={(e) => updateBs({ limit: Number(e.target.value) })}
            >
              <option value={4}>4 productos</option>
              <option value={8}>8 productos</option>
              <option value={12}>12 productos</option>
              <option value={16}>16 productos</option>
            </select>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label small fw-bold text-dark mb-1">Columnas por fila</label>
            <select
              className="form-select form-select-sm rounded-3"
              value={bs.columnsPerRow || 4}
              onChange={(e) => updateBs({ columnsPerRow: Number(e.target.value) })}
            >
              <option value={3}>3 Columnas</option>
              <option value={4}>4 Columnas</option>
              <option value={5}>5 Columnas</option>
              <option value={6}>6 Columnas</option>
            </select>
          </div>
        </div>

        {/* Selección manual de artículos */}
        {bs.selectionMode === 'manual' && (
          <div className="p-3 bg-light rounded-3 border">
            <label className="form-label small fw-bold text-dark mb-2 d-flex align-items-center gap-2">
              <i className="bi bi-search text-primary"></i>
              <span>Buscar artículos para sumar a "¡Los Más Vendidos!":</span>
            </label>
            <div className="input-group input-group-sm mb-2">
              <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por código o descripción..."
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {query && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => { setQuery(''); setResults([]); }}>
                  Limpiar
                </button>
              )}
            </div>

            {searching && <div className="small text-muted py-1">Buscando en catálogo...</div>}

            {results.length > 0 && (
              <div className="list-group mb-3 border rounded-3 shadow-xs" style={{ maxHeight: '190px', overflowY: 'auto' }}>
                {results.map(art => {
                  const exists = (bs.selectedProducts || []).some(p => String(p.c_artic) === String(art.c_artic));
                  return (
                    <button
                      key={art.c_artic}
                      type="button"
                      className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-2 px-3 small"
                      onClick={() => {
                        if (exists) return;
                        updateBs({
                          selectedProducts: [
                            ...(bs.selectedProducts || []),
                            { c_artic: art.c_artic, d_artic: art.d_artic, c_rubro: art.c_rubro }
                          ]
                        });
                      }}
                    >
                      <span><code>{art.c_artic}</code> — <strong>{art.d_artic}</strong></span>
                      <span className={`badge rounded-pill px-2 py-1 ${exists ? 'bg-success' : 'bg-primary'}`}>
                        {exists ? 'Seleccionado' : '+ Agregar'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="small fw-bold text-dark mb-2">
              Artículos en "¡Los Más Vendidos!" ({(bs.selectedProducts || []).length}):
            </div>
            {(bs.selectedProducts || []).length === 0 ? (
              <div className="p-3 bg-white rounded-3 border text-center text-muted small">
                Todavía no agregaste artículos manualmente. Si queda vacío, mostrará automáticamente los Artículos Destacados.
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {(bs.selectedProducts || []).map((p, idx) => (
                  <span
                    key={p.c_artic || idx}
                    className="badge bg-white text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 shadow-2xs"
                  >
                    <code className="text-primary">{p.c_artic}</code>
                    <span className="text-truncate" style={{ maxWidth: '210px' }}>{p.d_artic}</span>
                    <i
                      className="bi bi-x-circle-fill text-danger cursor-pointer"
                      onClick={() => {
                        const next = (bs.selectedProducts || []).filter((_, i) => i !== idx);
                        updateBs({ selectedProducts: next });
                      }}
                    ></i>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Selección por rubros */}
        {bs.selectionMode === 'rubros' && (
          <div className="p-3 bg-light rounded-3 border">
            <label className="form-label small fw-bold text-dark mb-2">
              Seleccioná qué rubros integran "¡Los Más Vendidos!" ({(bs.selectedRubros || []).length}):
            </label>
            <div className="row g-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {rubros.map(r => {
                const code = String(r.c_rubro).trim();
                const checked = (bs.selectedRubros || []).map(String).includes(code);
                return (
                  <div key={code} className="col-12 col-sm-6 col-md-4">
                    <label className="form-check d-flex align-items-center gap-2 small bg-white px-3 py-2 rounded-2 border cursor-pointer mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={checked}
                        onChange={(e) => {
                          const curr = (bs.selectedRubros || []).map(String);
                          const next = e.target.checked ? [...curr, code] : curr.filter(c => c !== code);
                          updateBs({ selectedRubros: next });
                        }}
                      />
                      <span className="text-truncate">{r.d_rubro}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 3. PANEL ADMINISTRADOR DE PREGUNTAS FRECUENTES (FAQ)
 */
export const FaqAdminPanel = ({
  config,
  setConfig,
  onToggleHomeSection,
  isHomeSectionEnabled,
  onToggleNavbarItem,
  isNavbarItemEnabled
}) => {
  const faq = {
    ...DEFAULT_FAQ_SECTION,
    ...(config.faqSection || {}),
    items: Array.isArray(config.faqSection?.items) ? config.faqSection.items : DEFAULT_FAQ_SECTION.items
  };

  const updateFaq = (patch) => {
    setConfig(prev => ({
      ...prev,
      faqSection: {
        ...DEFAULT_FAQ_SECTION,
        ...(prev.faqSection || {}),
        ...patch
      }
    }));
  };

  const handleAddQuestion = () => {
    const next = [
      ...(faq.items || []),
      {
        id: `faq-${Date.now()}`,
        category: 'General',
        question: '¿Nueva pregunta frecuente?',
        answer: 'Escribí acá la respuesta detallada para tus clientes.',
        is_active: true
      }
    ];
    updateFaq({ items: next });
  };

  const handleUpdateQuestion = (idx, patch) => {
    const next = [...(faq.items || [])];
    next[idx] = { ...next[idx], ...patch };
    updateFaq({ items: next });
  };

  const handleRemoveQuestion = (idx) => {
    const next = (faq.items || []).filter((_, i) => i !== idx);
    updateFaq({ items: next });
  };

  const handleMoveQuestion = (idx, dir) => {
    const target = idx + dir;
    const list = [...(faq.items || [])];
    if (target < 0 || target >= list.length) return;
    const [moved] = list.splice(idx, 1);
    list.splice(target, 0, moved);
    updateFaq({ items: list });
  };

  // Verificar si está en la barra secundaria
  const secItems = config.secondaryNavBar?.items || DEFAULT_SECONDARY_NAVBAR.items || [];
  const isFaqInSecBar = secItems.some(i => i && i.enabled !== false && (i.targetType === 'faq' || i.targetValue === '/preguntas-frecuentes'));

  const handleToggleFaqInSecBar = (checked) => {
    const currentSec = {
      ...DEFAULT_SECONDARY_NAVBAR,
      ...(config.secondaryNavBar || {}),
      items: Array.isArray(config.secondaryNavBar?.items)
        ? [...config.secondaryNavBar.items]
        : [...DEFAULT_SECONDARY_NAVBAR.items]
    };

    const existingIdx = currentSec.items.findIndex(
      i => i && (i.targetType === 'faq' || i.targetValue === '/preguntas-frecuentes')
    );

    if (existingIdx >= 0) {
      currentSec.items[existingIdx] = { ...currentSec.items[existingIdx], enabled: checked };
    } else if (checked) {
      currentSec.items.push({
        id: `sec-faq-${Date.now()}`,
        label: 'Preguntas frecuentes',
        enabled: true,
        type: 'direct',
        targetType: 'faq',
        targetValue: '/preguntas-frecuentes',
        highlight: false,
        badgeText: ''
      });
    }

    setConfig(prev => ({
      ...prev,
      secondaryNavBar: currentSec
    }));
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
        <div className="card-header bg-white py-3 px-4 border-bottom d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="bg-primary-subtle text-primary rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: '42px', height: '42px' }}
            >
              <i className="bi bi-patch-question-fill fs-5"></i>
            </div>
            <div>
              <h5 className="fw-bold text-dark mb-0">Preguntas Frecuentes (FAQ) — Configuración Modular</h5>
              <small className="text-muted">
                Elegí dónde querés mostrar las Preguntas Frecuentes y administrá todas las preguntas y respuestas.
              </small>
            </div>
          </div>

          <a
            href="/preguntas-frecuentes"
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-primary btn-sm rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
          >
            <i className="bi bi-box-arrow-up-right"></i>
            <span>Ver página /preguntas-frecuentes</span>
          </a>
        </div>

        <div className="card-body p-4">
          {/* Dónde mostrar Preguntas Frecuentes (4 switches independientes) */}
          <div className="p-3 p-md-4 bg-light rounded-4 border mb-4">
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <i className="bi bi-toggles text-primary"></i>
              <span>¿Dónde querés activar Preguntas Frecuentes?</span>
            </h6>

            <div className="row g-3">
              {/* 1. Página dedicada */}
              <div className="col-12 col-md-6 col-xl-3">
                <div className="p-3 bg-white rounded-3 border h-100 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold small text-dark">1. Módulo General Activo</span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        checked={faq.enabled !== false}
                        onChange={(e) => updateFaq({ enabled: e.target.checked })}
                      />
                    </div>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Habilita la página <code>/preguntas-frecuentes</code> y el contenido del centro de ayuda.
                  </small>
                </div>
              </div>

              {/* 2. En el Menú Superior Principal */}
              <div className="col-12 col-md-6 col-xl-3">
                <div className="p-3 bg-white rounded-3 border h-100 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold small text-dark">2. En Menú Principal (Arriba)</span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        checked={Boolean(isNavbarItemEnabled || faq.showInNavbar)}
                        onChange={(e) => {
                          updateFaq({ showInNavbar: e.target.checked });
                          if (onToggleNavbarItem) onToggleNavbarItem('faq', e.target.checked);
                        }}
                      />
                    </div>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Agrega el botón "PREGUNTAS FRECUENTES" en la barra de navegación superior principal.
                  </small>
                </div>
              </div>

              {/* 3. En la Barra Secundaria Celeste */}
              <div className="col-12 col-md-6 col-xl-3">
                <div className="p-3 bg-white rounded-3 border h-100 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold small text-dark">3. En Barra Secundaria</span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        checked={Boolean(isFaqInSecBar)}
                        onChange={(e) => handleToggleFaqInSecBar(e.target.checked)}
                      />
                    </div>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Muestra el botón directo "Preguntas frecuentes" en la nueva barra secundaria de categorías.
                  </small>
                </div>
              </div>

              {/* 4. Como Sección en la Portada de Inicio */}
              <div className="col-12 col-md-6 col-xl-3">
                <div className="p-3 bg-white rounded-3 border h-100 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold small text-dark">4. Como Bloque en Inicio</span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        checked={Boolean(isHomeSectionEnabled)}
                        onChange={(e) => {
                          if (onToggleHomeSection) onToggleHomeSection('faq', e.target.checked);
                        }}
                      />
                    </div>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Incrusta el bloque de Preguntas Frecuentes directamente en la Portada de Inicio.
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Textos y Opciones del Bloque FAQ */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <label className="form-label small fw-bold text-dark mb-1">Título Principal</label>
              <input
                type="text"
                className="form-control form-control-sm fw-bold rounded-3"
                value={faq.title || ''}
                onChange={(e) => updateFaq({ title: e.target.value })}
                placeholder="Preguntas Frecuentes"
              />
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label small fw-bold text-dark mb-1">Subtítulo / Bajada</label>
              <input
                type="text"
                className="form-control form-control-sm rounded-3"
                value={faq.subtitle || ''}
                onChange={(e) => updateFaq({ subtitle: e.target.value })}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label small fw-bold text-dark mb-1">Etiqueta Superior (Badge)</label>
              <input
                type="text"
                className="form-control form-control-sm rounded-3"
                value={faq.badgeText || ''}
                onChange={(e) => updateFaq({ badgeText: e.target.value })}
              />
            </div>

            <div className="col-12 col-md-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input cursor-pointer"
                  type="checkbox"
                  role="switch"
                  id="faqShowSearchSwitch"
                  checked={faq.showSearch !== false}
                  onChange={(e) => updateFaq({ showSearch: e.target.checked })}
                />
                <label className="form-check-label small fw-semibold cursor-pointer ms-1" htmlFor="faqShowSearchSwitch">
                  Mostrar buscador instantáneo de dudas
                </label>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input cursor-pointer"
                  type="checkbox"
                  role="switch"
                  id="faqShowCatsSwitch"
                  checked={faq.showCategories !== false}
                  onChange={(e) => updateFaq({ showCategories: e.target.checked })}
                />
                <label className="form-check-label small fw-semibold cursor-pointer ms-1" htmlFor="faqShowCatsSwitch">
                  Mostrar filtros por categoría
                </label>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input cursor-pointer"
                  type="checkbox"
                  role="switch"
                  id="faqShowWaSwitch"
                  checked={faq.showWhatsappCta !== false}
                  onChange={(e) => updateFaq({ showWhatsappCta: e.target.checked })}
                />
                <label className="form-check-label small fw-semibold cursor-pointer ms-1" htmlFor="faqShowWaSwitch">
                  Mostrar botón de consulta por WhatsApp al pie
                </label>
              </div>
            </div>
          </div>

          {/* Listado de Preguntas y Respuestas */}
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
            <div>
              <h6 className="fw-bold text-dark mb-0">
                Listado de Preguntas y Respuestas ({(faq.items || []).length})
              </h6>
              <small className="text-muted">Podés agregar, quitar, ocultar o cambiar el orden de todas las preguntas.</small>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill px-3 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow-xs"
              onClick={handleAddQuestion}
            >
              <i className="bi bi-plus-circle-fill"></i>
              <span>Agregar Pregunta</span>
            </button>
          </div>

          {(faq.items || []).length === 0 ? (
            <div className="text-center p-4 bg-light rounded-4 border text-muted small">
              No hay preguntas cargadas. Hacé clic en <strong>"Agregar Pregunta"</strong> para crear la primera.
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {(faq.items || []).map((item, idx) => (
                <div
                  key={item.id || idx}
                  className={`p-3 rounded-4 border ${
                    item.is_active !== false ? 'bg-white border-secondary-subtle shadow-2xs' : 'bg-light opacity-75'
                  }`}
                >
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-2 pb-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-light border px-2 py-1"
                          disabled={idx === 0}
                          onClick={() => handleMoveQuestion(idx, -1)}
                        >
                          <i className="bi bi-chevron-up"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-light border px-2 py-1"
                          disabled={idx === (faq.items || []).length - 1}
                          onClick={() => handleMoveQuestion(idx, 1)}
                        >
                          <i className="bi bi-chevron-down"></i>
                        </button>
                      </div>
                      <span className="badge bg-primary text-white rounded-pill px-3 py-1">Pregunta #{idx + 1}</span>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          role="switch"
                          checked={item.is_active !== false}
                          onChange={(e) => handleUpdateQuestion(idx, { is_active: e.target.checked })}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '30px', height: '30px', padding: 0 }}
                        onClick={() => handleRemoveQuestion(idx)}
                        title="Eliminar pregunta"
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-12 col-md-8">
                      <label className="form-label small fw-bold text-muted mb-1">Pregunta</label>
                      <input
                        type="text"
                        className="form-control form-control-sm fw-bold rounded-3"
                        value={item.question || ''}
                        onChange={(e) => handleUpdateQuestion(idx, { question: e.target.value })}
                        placeholder="Ej: ¿Realizan envíos a todo el país?"
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small fw-bold text-muted mb-1">Categoría (opcional)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm rounded-3"
                        value={item.category || ''}
                        onChange={(e) => handleUpdateQuestion(idx, { category: e.target.value })}
                        placeholder="Ej: Envíos, Pagos, Mayoristas..."
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold text-muted mb-1">Respuesta</label>
                      <textarea
                        className="form-control form-control-sm rounded-3"
                        rows="2"
                        value={item.answer || ''}
                        onChange={(e) => handleUpdateQuestion(idx, { answer: e.target.value })}
                        placeholder="Escribí la respuesta clara para el cliente..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
